export const ROUTE_PATHS = {
  HOME: '/',
  SERVICES: '/services',
  ABOUT: '/a-propos',
  FOR_LAWYERS: '/avocats',
  ADOPTION: '/adoption-internationale',
  BLOG: '/ressources',
  GUIDES: '/guides',
  VIDEOS: '/videos',
  FAQ: '/faq',
  CONTACT: '/contact',
} as const;

export interface ServiceType {
  id: string;
  titre: string;
  description: string;
  icone: string;
  details: {
    descriptionComplete: string;
    processus: Array<{
      etape: number;
      titre: string;
      description: string;
    }>;
    pourQui: string[];
    duree?: string;
    subvention?: {
      titre: string;
      texte: string;
      lien: string;
      lienTexte: string;
    };
  };
}

export interface TestimonialType {
  id: string;
  nom: string;
  role: string;
  contenu: string;
  note?: number;
}

export interface BlogArticleType {
  id: string;
  titre: string;
  slug: string;
  description: string;
  contenu: string;
  categorie: 'coparentalite' | 'mediation' | 'expertise' | 'enfants' | 'conseils';
  datePublication: string;
  tempsLecture: string;
  auteur: string;
}

export interface GuideType {
  id: string;
  titre: string;
  description: string;
  categorie: 'parents' | 'avocats' | 'general';
  format: 'PDF' | 'Document';
  taille: string;
  urlTelechargement: string;
}

export interface VideoType {
  id: string;
  titre: string;
  description: string;
  type: 'presentation' | 'temoignage' | 'conseil';
  duree: string;
  urlVideo: string;
  thumbnail: string;
}

export interface FAQType {
  id: string;
  question: string;
  reponse: string;
  categorie: 'parents' | 'avocats' | 'processus';
}

export const SERVICES_DATA: ServiceType[] = [
  {
    id: 'expertise-psychosociale',
    titre: 'Expertise psychosociale',
    description: 'Évaluation professionnelle rigoureuse en matière de garde d\'enfants, de droits d\'accès et de capacités parentales dans un contexte judiciaire.',
    icone: 'Scale',
    details: {
      descriptionComplete: 'L\'expertise psychosociale est une évaluation approfondie réalisée par une professionnelle qualifiée dans le cadre de procédures judiciaires familiales. Elle vise à formuler des recommandations éclairées concernant la garde des enfants, les droits d\'accès et l\'exercice de l\'autorité parentale, toujours dans l\'intérêt supérieur de l\'enfant.',
      processus: [
        {
          etape: 1,
          titre: 'Mandat et acceptation',
          description: 'Réception du mandat judiciaire, analyse des documents légaux et acceptation formelle de l\'expertise.',
        },
        {
          etape: 2,
          titre: 'Collecte d\'information',
          description: 'Rencontres individuelles avec les parents, observations parent-enfant, consultation de documents pertinents et contacts avec les intervenants impliqués.',
        },
        {
          etape: 3,
          titre: 'Évaluation et analyse',
          description: 'Analyse approfondie des capacités parentales, du système familial, des besoins de l\'enfant et des facteurs de risque ou de protection.',
        },
        {
          etape: 4,
          titre: 'Rédaction du rapport',
          description: 'Élaboration d\'un rapport détaillé incluant les observations, l\'analyse clinique et les recommandations professionnelles.',
        },
        {
          etape: 5,
          titre: 'Dépôt et suivi',
          description: 'Dépôt du rapport au tribunal et disponibilité pour témoignage ou clarifications si requis.',
        },
      ],
      pourQui: [
        'Parents en litige concernant la garde ou les droits d\'accès',
        'Avocats mandatant une évaluation objective',
        'Tribunal nécessitant une expertise professionnelle',
        'Situations de haut conflit ou de préoccupations pour la sécurité de l\'enfant',
      ],
      duree: '4 à 6 semaines selon la complexité',
    },
  },
  {
    id: 'mediation-familiale',
    titre: 'Médiation familiale',
    description: 'Accompagnement neutre et structuré pour faciliter la résolution de conflits familiaux à l\'amiable, notamment en matière de garde, pension alimentaire et partage des biens.',
    icone: 'Users',
    details: {
      descriptionComplete: 'La médiation familiale est un processus volontaire et confidentiel où un médiateur neutre aide les parents à trouver des solutions mutuellement acceptables concernant leur séparation. Ce service favorise la communication constructive et la prise de décisions éclairées dans l\'intérêt de toute la famille.',
      processus: [
        {
          etape: 1,
          titre: 'Séance d\'information',
          description: 'Présentation du processus de médiation, des règles de confidentialité et des objectifs à atteindre.',
        },
        {
          etape: 2,
          titre: 'Identification des enjeux',
          description: 'Clarification des points de désaccord et des besoins de chaque parent et des enfants.',
        },
        {
          etape: 3,
          titre: 'Exploration des options',
          description: 'Recherche collaborative de solutions possibles et évaluation de leurs impacts sur la famille.',
        },
        {
          etape: 4,
          titre: 'Négociation et entente',
          description: 'Facilitation des discussions pour parvenir à des accords équilibrés et durables.',
        },
        {
          etape: 5,
          titre: 'Remise du résumé des ententes de médiation',
          description: '',
        },
      ],
      pourQui: [
        'Parents souhaitant éviter un litige judiciaire',
        'Couples désirant maintenir une relation coparentale fonctionnelle',
        'Familles cherchant des solutions personnalisées et flexibles',
        'Parents référés par le tribunal ou leurs avocats',
      ],
      subvention: {
        titre: 'Subvention gouvernementale disponible',
        texte: 'Au Québec, le gouvernement subventionne la médiation familiale pour les parents ayant eu des enfants ensemble (mariés ou non). Chaque parent a droit à un certain nombre de séances gratuites ou à coût réduit, selon votre situation. Cette aide financière vise à rendre la médiation accessible à toutes les familles et à favoriser des ententes à l\'amiable dans l\'intérêt des enfants.',
        lien: 'https://juridiqc.gouv.qc.ca/separation-et-divorce/mediation/la-mediation-familiale-combien-ca-coute',
        lienTexte: 'En savoir plus sur le financement de la médiation familiale',
      },
    },
  },
  {
    id: 'coaching-coparental',
    titre: 'Coaching coparental',
    description: 'Accompagnement pratique et personnalisé pour améliorer la communication et la collaboration entre coparents, réduire les conflits et favoriser le bien-être des enfants.',
    icone: 'MessageCircle',
    details: {
      descriptionComplete: 'Le coaching coparental offre un soutien concret aux parents séparés pour développer des compétences de communication efficaces, établir des routines coparentales fonctionnelles et gérer les situations conflictuelles de manière constructive. L\'objectif est de créer un environnement stable et sécurisant pour les enfants malgré la séparation.',
      processus: [
        {
          etape: 1,
          titre: 'Évaluation initiale',
          description: 'Analyse de la dynamique coparentale actuelle, identification des défis et définition des objectifs.',
        },
        {
          etape: 2,
          titre: 'Développement de stratégies',
          description: 'Élaboration d\'outils de communication, de techniques de gestion de conflits et de plans d\'action concrets.',
        },
        {
          etape: 3,
          titre: 'Mise en pratique',
          description: 'Application des stratégies dans le quotidien avec suivi et ajustements selon les résultats.',
        },
        {
          etape: 4,
          titre: 'Consolidation',
          description: 'Renforcement des acquis, prévention des rechutes et préparation à l\'autonomie coparentale.',
        },
      ],
      pourQui: [
        'Parents en coparentalité difficile ou conflictuelle',
        'Familles recomposées cherchant à harmoniser les dynamiques',
        'Parents souhaitant améliorer leur communication pour le bien des enfants',
        'Situations de tensions récurrentes nécessitant un encadrement professionnel',
      ],
      duree: '6 à 12 séances selon les besoins',
    },
  },
  {
    id: 'retablissement-lien',
    titre: 'Rétablissement du lien parent-enfant',
    description: 'Intervention spécialisée pour restaurer ou renforcer la relation parent-enfant dans des contextes de rupture de contact, refus de visite ou allégations d\'aliénation parentale.',
    icone: 'Heart',
    details: {
      descriptionComplete: 'Le service de rétablissement du lien parent-enfant s\'adresse aux familles confrontées à des situations complexes où la relation entre un parent et son enfant est compromise ou rompue. Cette intervention clinique structurée vise à reconstruire progressivement un lien sain et sécurisant, en tenant compte des besoins de l\'enfant et des dynamiques familiales.',
      processus: [
        {
          etape: 1,
          titre: 'Évaluation de la situation',
          description: 'Analyse approfondie des causes de la rupture, des facteurs contributifs et des ressources disponibles.',
        },
        {
          etape: 2,
          titre: 'Plan d\'intervention',
          description: 'Élaboration d\'un plan progressif et adapté pour favoriser le rapprochement parent-enfant.',
        },
        {
          etape: 3,
          titre: 'Rencontres thérapeutiques',
          description: 'Séances structurées et encadrées pour reconstruire la confiance et la communication.',
        },
        {
          etape: 4,
          titre: 'Transition progressive',
          description: 'Augmentation graduelle des contacts et autonomisation de la relation parent-enfant.',
        },
        {
          etape: 5,
          titre: 'Suivi et consolidation',
          description: 'Accompagnement pour maintenir les acquis et prévenir les rechutes.',
        },
      ],
      pourQui: [
        'Enfants refusant le contact avec un parent',
        'Situations de rupture relationnelle prolongée',
        'Contextes d\'allégations d\'aliénation parentale',
        'Familles nécessitant un encadrement professionnel pour rétablir le lien',
      ],
      duree: '3 à 6 mois selon la complexité',
    },
  },
];

export const FAQ_DATA: FAQType[] = [
  {
    id: 'faq-1',
    question: 'Quelle est la différence entre une expertise psychosociale et une médiation familiale?',
    reponse: 'L\'expertise psychosociale est une évaluation professionnelle mandatée par le tribunal pour formuler des recommandations objectives sur la garde et les droits d\'accès. Elle est réalisée dans un contexte judiciaire et aboutit à un rapport déposé au tribunal. La médiation familiale, quant à elle, est un processus volontaire et confidentiel où les parents travaillent ensemble avec un médiateur neutre pour trouver des solutions mutuellement acceptables. La médiation favorise la collaboration, tandis que l\'expertise fournit une évaluation indépendante pour le tribunal.',
    categorie: 'parents',
  },
  {
    id: 'faq-2',
    question: 'Combien de temps dure une expertise psychosociale?',
    reponse: 'Une expertise psychosociale complète prend généralement entre 4 et 6 semaines, selon la complexité de la situation familiale, le nombre d\'intervenants à consulter et la disponibilité des parties. Ce délai inclut les rencontres avec les parents et les enfants, la collecte de documents, les consultations avec d\'autres professionnels si nécessaire, l\'analyse approfondie et la rédaction du rapport détaillé.',
    categorie: 'processus',
  },
  {
    id: 'faq-4',
    question: 'Comment se déroule une première consultation?',
    reponse: 'La première consultation permet de comprendre votre situation, d\'identifier vos besoins et de déterminer le service le plus approprié. Lors de cette rencontre, nous discutons de vos préoccupations, expliquons le processus applicable à votre situation, répondons à vos questions et établissons un plan d\'action. Cette consultation est confidentielle et se déroule dans un environnement professionnel et rassurant. Elle dure généralement entre 60 et 90 minutes.',
    categorie: 'processus',
  },
  {
    id: 'faq-5',
    question: 'Puis-je mandater une expertise psychosociale sans passer par le tribunal?',
    reponse: 'Non, une expertise psychosociale formelle est généralement mandatée par le tribunal dans le cadre d\'une procédure judiciaire. Cependant, si vous souhaitez obtenir une évaluation professionnelle en dehors du contexte judiciaire, nous offrons des services de consultation et d\'évaluation parentale qui peuvent vous fournir des recommandations et un accompagnement sans être déposés au tribunal. Votre avocat peut vous conseiller sur l\'option la plus appropriée à votre situation.',
    categorie: 'avocats',
  },
  {
    id: 'faq-6',
    question: 'Que se passe-t-il si mon enfant refuse de voir l\'autre parent?',
    reponse: 'Le refus d\'un enfant de voir un parent est une situation complexe qui nécessite une évaluation professionnelle. Nos services de rétablissement du lien parent-enfant sont spécifiquement conçus pour ces situations. Nous analysons les causes du refus, évaluons les dynamiques familiales et mettons en place un plan d\'intervention progressif et adapté pour favoriser le rapprochement. L\'objectif est toujours de protéger l\'intérêt de l\'enfant tout en travaillant à restaurer une relation saine avec les deux parents.',
    categorie: 'parents',
  },
  {
    id: 'faq-8',
    question: 'Quels documents dois-je préparer pour une expertise?',
    reponse: 'Pour une expertise psychosociale, vous devrez fournir les documents judiciaires pertinents (ordonnances, jugements antérieurs), les dossiers scolaires et médicaux des enfants (avec consentement), tout rapport d\'évaluation ou d\'intervention antérieur, et tout autre document pertinent à la situation familiale. Une liste détaillée vous sera fournie lors de l\'acceptation du mandat. Il est important de rassembler ces documents dès le début du processus pour faciliter l\'évaluation.',
    categorie: 'processus',
  },
  {
    id: 'faq-9',
    question: 'Puis-je référer un client pour une expertise psychosociale?',
    reponse: 'Oui, les avocats et professionnels du droit peuvent référer leurs clients pour une expertise psychosociale. Nous travaillons en étroite collaboration avec les avocats pour assurer une communication claire, le respect des délais et la qualité des rapports. Pour référer un client, contactez-nous avec les informations de base sur le mandat et nous vous fournirons les documents nécessaires et un calendrier préliminaire. Nous sommes disponibles pour discuter des particularités de chaque dossier.',
    categorie: 'avocats',
  },
  {
    id: 'faq-10',
    question: 'Le coaching coparental peut-il remplacer la médiation?',
    reponse: 'Le coaching coparental et la médiation sont des services complémentaires mais distincts. La médiation vise à résoudre des différends spécifiques et à établir des ententes formelles, tandis que le coaching coparental se concentre sur le développement de compétences de communication et de collaboration à long terme. Le coaching est particulièrement utile après une médiation pour aider les parents à mettre en pratique leurs ententes, ou dans des situations de coparentalité difficile où des conflits récurrents nécessitent un accompagnement continu.',
    categorie: 'parents',
  },
  {
    id: 'faq-11',
    question: 'Offrez-vous des services en anglais?',
    reponse: 'Nos services sont principalement offerts en français. Cependant, nous pouvons accommoder certaines demandes en anglais selon la disponibilité et la nature du service. N\'hésitez pas à nous contacter pour discuter de vos besoins linguistiques et nous trouverons la meilleure solution pour vous accompagner.',
    categorie: 'processus',
  },
  {
    id: 'faq-12',
    question: 'Quelle est votre zone de service?',
    reponse: 'Nous desservons principalement la région de l\'Outaouais, incluant Gatineau et les municipalités environnantes. Selon la nature du mandat et les circonstances, nous pouvons également offrir nos services dans d\'autres régions du Québec. Pour les expertises psychosociales mandatées par le tribunal, nous acceptons les mandats provenant de différentes régions. Contactez-nous pour vérifier si nous pouvons répondre à vos besoins géographiques.',
    categorie: 'processus',
  },
];

export const TESTIMONIALS_DATA: TestimonialType[] = [
  {
    id: 'temoignage-1',
    nom: 'Marie L.',
    role: 'Parent',
    contenu: 'Le coaching coparental m\'a permis de retrouver une communication apaisée avec mon ex-conjoint. Nos enfants sont maintenant beaucoup plus sereins. L\'approche professionnelle et bienveillante m\'a vraiment aidée à traverser cette période difficile.',
    note: 5,
  },
  {
    id: 'temoignage-2',
    nom: 'Jean-François D.',
    role: 'Parent',
    contenu: 'Grâce au service de rétablissement du lien, j\'ai pu reconstruire une relation avec ma fille après deux ans de rupture de contact. Le processus a été progressif et respectueux. Je recommande vivement ces services à toute famille en difficulté.',
    note: 5,
  },
  {
    id: 'temoignage-3',
    nom: 'Me Sophie Tremblay',
    role: 'Avocate en droit familial',
    contenu: 'Je réfère régulièrement mes clients au Centre d\'expertise familiale de l\'Outaouais. Les rapports d\'expertise sont rigoureux, complets et respectent toujours les délais. La communication avec l\'équipe est excellente et professionnelle.',
    note: 5,
  },
  {
    id: 'temoignage-4',
    nom: 'Caroline B.',
    role: 'Parent',
    contenu: 'La médiation familiale nous a permis d\'éviter un long processus judiciaire. Nous avons trouvé des solutions qui conviennent à toute la famille, dans le respect de chacun. Le médiateur a su créer un climat de confiance et de collaboration.',
    note: 5,
  },
  {
    id: 'temoignage-5',
    nom: 'Me Alexandre Gagnon',
    role: 'Avocat',
    contenu: 'L\'expertise psychosociale réalisée pour mon client était d\'une qualité exceptionnelle. L\'analyse était approfondie, objective et a grandement aidé le tribunal à prendre une décision éclairée dans l\'intérêt de l\'enfant.',
    note: 5,
  },
  {
    id: 'temoignage-6',
    nom: 'Isabelle M.',
    role: 'Parent',
    contenu: 'Après une séparation très conflictuelle, le coaching coparental nous a donné les outils pour mieux communiquer. Nos enfants ne sont plus pris au milieu de nos disputes. C\'est un vrai soulagement pour toute la famille.',
    note: 5,
  },
];

export const BLOG_ARTICLES_DATA: BlogArticleType[] = [
  {
    id: 'article-1',
    titre: 'Comment gérer la coparentalité après une séparation difficile',
    slug: 'gerer-coparentalite-separation',
    description: 'Conseils pratiques pour maintenir une relation coparentale fonctionnelle malgré les tensions et protéger le bien-être de vos enfants.',
    contenu: 'La séparation est une période difficile pour toute la famille, mais il est possible de maintenir une coparentalité saine...',
    categorie: 'coparentalite',
    datePublication: '2026-03-15',
    tempsLecture: '8 min',
    auteur: 'Centre d\'expertise familiale',
  },
  {
    id: 'article-2',
    titre: 'Les 5 erreurs à éviter en médiation familiale',
    slug: 'erreurs-mediation-familiale',
    description: 'Découvrez les pièges courants qui peuvent compromettre le succès de votre médiation et comment les éviter.',
    contenu: 'La médiation familiale est un processus délicat qui nécessite une préparation adéquate...',
    categorie: 'mediation',
    datePublication: '2026-03-10',
    tempsLecture: '6 min',
    auteur: 'Centre d\'expertise familiale',
  },
  {
    id: 'article-3',
    titre: 'Comprendre l\'expertise psychosociale : guide pour les parents',
    slug: 'comprendre-expertise-psychosociale',
    description: 'Tout ce que vous devez savoir sur le processus d\'expertise psychosociale, de la première rencontre au rapport final.',
    contenu: 'L\'expertise psychosociale peut sembler intimidante, mais comprendre le processus aide à mieux s\'y préparer...',
    categorie: 'expertise',
    datePublication: '2026-03-05',
    tempsLecture: '10 min',
    auteur: 'Centre d\'expertise familiale',
  },
  {
    id: 'article-4',
    titre: 'Protéger vos enfants pendant une séparation : 10 conseils essentiels',
    slug: 'proteger-enfants-separation',
    description: 'Comment minimiser l\'impact de la séparation sur vos enfants et maintenir leur sentiment de sécurité.',
    contenu: 'Les enfants sont souvent les plus affectés par une séparation. Voici comment les protéger...',
    categorie: 'enfants',
    datePublication: '2026-02-28',
    tempsLecture: '7 min',
    auteur: 'Centre d\'expertise familiale',
  },
  {
    id: 'article-5',
    titre: 'Quand consulter pour un rétablissement du lien parent-enfant?',
    slug: 'retablissement-lien-parent-enfant',
    description: 'Signes qu\'il est temps de consulter un professionnel pour restaurer la relation avec votre enfant.',
    contenu: 'Le refus de contact d\'un enfant avec un parent est une situation complexe qui nécessite une intervention...',
    categorie: 'conseils',
    datePublication: '2026-02-20',
    tempsLecture: '9 min',
    auteur: 'Centre d\'expertise familiale',
  },
  {
    id: 'article-6',
    titre: 'Communication efficace en coparentalité : techniques éprouvées',
    slug: 'communication-coparentalite',
    description: 'Stratégies concrètes pour améliorer la communication avec votre coparent et réduire les conflits.',
    contenu: 'Une communication claire et respectueuse est la clé d\'une coparentalité réussie...',
    categorie: 'coparentalite',
    datePublication: '2026-02-15',
    tempsLecture: '8 min',
    auteur: 'Centre d\'expertise familiale',
  },
];

export const GUIDES_DATA: GuideType[] = [
  {
    id: 'guide-1',
    titre: 'Guide de préparation à l\'expertise psychosociale',
    description: 'Document complet pour vous préparer à une expertise psychosociale : documents à rassembler, déroulement des rencontres, questions fréquentes.',
    categorie: 'parents',
    format: 'PDF',
    taille: '2.5 MB',
    urlTelechargement: '#',
  },
  {
    id: 'guide-2',
    titre: 'Trousse de coparentalité harmonieuse',
    description: 'Outils pratiques et modèles de communication pour faciliter la coparentalité : calendrier partagé, journal de communication, règles de base.',
    categorie: 'parents',
    format: 'PDF',
    taille: '1.8 MB',
    urlTelechargement: '#',
  },
  {
    id: 'guide-3',
    titre: 'Parler de la séparation à vos enfants',
    description: 'Guide pratique avec des conseils adaptés selon l\'âge de l\'enfant pour annoncer et expliquer la séparation de manière bienveillante.',
    categorie: 'parents',
    format: 'PDF',
    taille: '1.2 MB',
    urlTelechargement: '#',
  },
  {
    id: 'guide-4',
    titre: 'Référer un client pour une expertise psychosociale',
    description: 'Guide destiné aux avocats : processus de référence, documents requis, délais typiques, communication avec l\'expert.',
    categorie: 'avocats',
    format: 'PDF',
    taille: '800 KB',
    urlTelechargement: '#',
  },
  {
    id: 'guide-5',
    titre: 'Comprendre le rapport d\'expertise psychosociale',
    description: 'Explication détaillée de la structure et du contenu d\'un rapport d\'expertise pour mieux comprendre les recommandations.',
    categorie: 'general',
    format: 'PDF',
    taille: '1.5 MB',
    urlTelechargement: '#',
  },
  {
    id: 'guide-6',
    titre: 'Checklist de la médiation familiale',
    description: 'Liste de vérification complète pour vous préparer à la médiation : documents, questions à poser, points à négocier.',
    categorie: 'parents',
    format: 'PDF',
    taille: '600 KB',
    urlTelechargement: '#',
  },
];

export const VIDEOS_DATA: VideoType[] = [
  {
    id: 'video-1',
    titre: 'Présentation du Centre d\'expertise familiale de l\'Outaouais',
    description: 'Découvrez notre approche, nos valeurs et nos services dans cette vidéo de présentation complète.',
    type: 'presentation',
    duree: '5:30',
    urlVideo: '#',
    thumbnail: 'CONSULTATION_1',
  },
  {
    id: 'video-2',
    titre: 'Témoignage : Retrouver une coparentalité apaisée',
    description: 'Marie partage son expérience du coaching coparental et comment cela a transformé sa relation avec son ex-conjoint.',
    type: 'temoignage',
    duree: '4:15',
    urlVideo: '#',
    thumbnail: 'SERVICES_FAMILY_1',
  },
  {
    id: 'video-3',
    titre: 'Qu\'est-ce qu\'une expertise psychosociale?',
    description: 'Explication détaillée du processus d\'expertise psychosociale, de la première rencontre au rapport final.',
    type: 'presentation',
    duree: '7:45',
    urlVideo: '#',
    thumbnail: 'CONSULTATION_2',
  },
  {
    id: 'video-4',
    titre: 'Conseils pour une communication coparentale efficace',
    description: 'Techniques pratiques pour améliorer la communication avec votre coparent et réduire les tensions.',
    type: 'conseil',
    duree: '6:20',
    urlVideo: '#',
    thumbnail: 'CONSULTATION_6',
  },
  {
    id: 'video-5',
    titre: 'Témoignage d\'un avocat : Pourquoi référer au CEFO',
    description: 'Me Tremblay explique pourquoi elle fait confiance au Centre pour ses mandats d\'expertise psychosociale.',
    type: 'temoignage',
    duree: '3:50',
    urlVideo: '#',
    thumbnail: 'CONSULTATION_7',
  },
  {
    id: 'video-6',
    titre: 'Le rétablissement du lien parent-enfant : comment ça fonctionne',
    description: 'Présentation du processus de rétablissement du lien et des étapes pour reconstruire la relation.',
    type: 'presentation',
    duree: '8:10',
    urlVideo: '#',
    thumbnail: 'SERVICES_FAMILY_2',
  },
];